import React, { useCallback } from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import cn from "classnames";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";

import styles from "../../NotificationsCommon.module.scss";

interface Props {
  form: FormInstance<NotificationSettingValue>;
}

const ActivityOwned: React.FC<Props> = ({ form }) => {
  const intl = useIntl();

  const enabledNamePath: string[] = ["notesAndActivities", "activityOwned", "enabled"];

  const handleToggle = useCallback(
    (name: "activityCompleted" | "activityTimeChanged" | "enabled") => (enabled: boolean) => {
      form.setFieldsValue({
        notesAndActivities: {
          activityOwned:
            name === "enabled"
              ? {
                  activityCompleted: enabled,
                  activityTimeChanged: enabled,
                  enabled,
                }
              : { [name]: enabled },
        },
      });
    },
    [form]
  );

  return (
    <div>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.notesAndActivities.enabled !== next.notesAndActivities.enabled ||
          prev.notesAndActivities.activityOwned.enabled !==
            next.notesAndActivities.activityOwned.enabled
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["notesAndActivities", "activityOwned", "enabled"])}
            className={cn(styles.bold, styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(["notesAndActivities", "enabled"])}
            onChange={handleToggle("enabled")}
          >
            {intl.formatMessage({
              id: "settings.notifications.notesAndActivities.activityOwned",
              defaultMessage: "Notify me when an activity I’ve created",
              description: "Settings notifications Notes and activities - activity owned - enabled",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.notesAndActivities.enabled !== next.notesAndActivities.enabled ||
          prev.notesAndActivities.activityOwned.enabled !==
            next.notesAndActivities.activityOwned.enabled ||
          prev.notesAndActivities.activityOwned.activityCompleted !==
            next.notesAndActivities.activityOwned.activityCompleted
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["notesAndActivities", "activityOwned", "activityCompleted"])}
            className={cn(
              styles.secondLevelSetting,
              styles.thirdLevelSetting,
              styles.secondaryCheckboxText
            )}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleToggle("activityCompleted")}
          >
            {intl.formatMessage({
              id: "settings.notifications.notesAndActivities.activityOwned.activityCompleted",
              defaultMessage: 'is marked as "Done"',
              description:
                "Settings notifications Notes and activities - activity owned - activity done",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.notesAndActivities.enabled !== next.notesAndActivities.enabled ||
          prev.notesAndActivities.activityOwned.enabled !==
            next.notesAndActivities.activityOwned.enabled ||
          prev.notesAndActivities.activityOwned.activityTimeChanged !==
            next.notesAndActivities.activityOwned.activityTimeChanged
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["notesAndActivities", "activityOwned", "activityTimeChanged"])}
            className={cn(
              styles.secondLevelSetting,
              styles.thirdLevelSetting,
              styles.secondaryCheckboxText
            )}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleToggle("activityTimeChanged")}
          >
            {intl.formatMessage({
              id: "settings.notifications.notesAndActivities.activityOwned.activityTimeChanged",
              defaultMessage: "has an updated start time",
              description:
                "Settings notifications Notes and activities - activity owned - activity Time Changed",
            })}
          </Checkbox>
        )}
      </Form.Item>
    </div>
  );
};

export default ActivityOwned;
